import {
  Component,
  computed,
  ElementRef,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as _ from 'underscore';

import * as Sentry from "@sentry/angular";

import { faMagnifyingGlass, faPeopleGroup } from '@fortawesome/free-solid-svg-icons';

import { ManagementUser } from '../../../core/models/management-user.model';
import { District } from '../../../core/models/district.model';
import { School } from '../../../core/models/school.model';
import { ReportsService } from '../../../core/services/reports.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { ChartConfiguration, ChartType, ChartTypeRegistry } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels' ;
import { BaseChartDirective } from 'ng2-charts';
import { PrintService } from '../../../core/services/print.service';
import { districtUsageIntroSteps } from './district-usage.intro-steps'
import { canChangeSchool, canViewSchoolFilter } from '../../utils';
import { IntroStep } from '../../../core/services/tour.service';

interface TeacherAggregateData {
  data: [
    string, // teacher first name
    string, // teacher last name
    number[], // teacher grades
    string, // school name
    number, // number of logins
    number, // % of students hitting min weekly time goal for this teacher
    number, // % of students hitting min weekly units goal for this teacher
  ];
  display: [
    string, // teacher first name
    string, // teacher last name
    string, // teacher grades
    string, // school name
    number, // number of logins
    string, // html template for percent of students hitting min weekly time goal
    string, // html template for percent of students hitting min weekly units goal
  ];
}

interface SchoolAggregateData {
  data: [
    string, // school name
    number, // number of teachers
    number, // number of students
    number, // % of students hitting min weekly time goal in the school
    number, // % of students hitting min weekly units goal in the school
  ];
  display: [
    string, // school name
    number, // number of teachers
    number, // number of students
    string, // html template for percent of students hitting min weekly time goal
    string, // html template for percent of students hitting min weekly units goal
  ];
}

@Component({
    selector: 'wf-district-usage',
    templateUrl: './usage.component.html',
    styleUrls: ['./usage.component.css'],
    standalone: false
})
export class DistrictUsageComponent implements OnInit {
  currentUser: ManagementUser | null = null ;
  showSchoolFilter: boolean = false ;
  areSessionsForAcademicYear: boolean = false ;
  schoolFilterError: string = '' ;
  shouldShowSchools: boolean = false;
  loadError: string = '' ;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  showTeacherTable: boolean = false ;
  showSchoolTable: boolean = false ;
  selectedDistrictId: number = -1 ;
  selectedSchoolId: number = 0 ;
  reportData: any ;
  rangeData: any ;
  enrolled: number = 0 ;
  startedPercent: string = '' ;
  loginPercentDisplay: string = '' ;
  multiLoginPercentDisplay: string = '' ;
  avgWeeklyUsage: number = 0 ;
  dateRanges: any[] = [] ;
  dateRange: any = {} ;
  schoolTableSwitch: string = '+' ;
  teacherTableSwitch: string = '+' ;
  schoolCols: string[] = [
    'School',
    'Number of Teachers',
    'WordFlight Students',
    '% students hitting min weekly time goal',
    '% students completing at least one unit per week',
  ] ;
  schoolAggregateData = signal<SchoolAggregateData[]>([]);
  teacherCols: string[] = [
    'First Name',
    'Last Name',
    'Grade',
    'School',
    '# of Teacher Logins',
    '% students hitting min weekly time goal',
    '% students completing at least one unit per week',
  ] ;
  teacherAggregateData = signal<TeacherAggregateData[]>([]);
  studentPercentThresholdForMinWeeklyTimeGoal = signal<number>(70);
  numSchoolsMeetingStudentMinWeeklyTimeGoalThreshold = computed(() => {
    return this.schoolAggregateData().filter(
      (school) => school.data[3] > this.studentPercentThresholdForMinWeeklyTimeGoal()
    ).length;
  });
  numTeachersMeetingStudentMinWeeklyTimeGoalThreshold = computed(() => {
    return this.teacherAggregateData().filter(
      (teacher) => teacher.data[5] > this.studentPercentThresholdForMinWeeklyTimeGoal()
    ).length;
  });
  trendType: ChartType = 'line' ;
  chartPlugins = [ ChartDataLabels ];
  trendData: ChartConfiguration['data'] = {
    datasets: [
      {
        data: [ ],
        borderColor: 'rgba(127, 127, 127, 1)',
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
        label: 'target',
      },
      {
        data: [ ],
        borderColor: 'rgba(127, 127, 127, 1)',
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
        label: 'min',
      },
      {
        data: [ ],
        borderColor: 'rgba(156, 193, 83, 1)',
        backgroundColor: 'rgba(224, 236, 201, 1)',
        borderWidth: 4,
        pointRadius: 0,
        fill: 'origin',
      }
    ],
    labels: [ '' ]
  } ;
  trendOpts: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    font: {
      family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
    },
    layout: {
      padding: {
        right: 36,
      },
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          drawOnChartArea: false,
          drawTicks: true,
        },
        ticks: {
          callback: function (value, index, values) {
            return `Week ${index + 1}` ;
          },
          padding: 12,
          display: true,
          font: {
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
            size: 10,
          }
        },
      },
      y: {
        grid: {
          display: false,
        },
        min: 0,
        max: 100,
        beginAtZero: true,
        title: {
          display: true,
          text: 'mean minutes per week',
          font: {
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
            size: 16,
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'start',
        align: 'end',
        color: 'black',
        display: (ctx) => ctx.dataset.label != null && ctx.dataIndex === ctx.dataset.data.length - 1,
        formatter: (v, ctx) => ctx.dataset.label,
        offset: 4,
        font: {
          size: 12
        },
      }
    },
  } ;
  // TODO: Type error during build when breakdownType is typed as ChartType. 'doughnut' is valid but not recognized during
  //       compilation from 'npm run watch'. Need to investigate why this is happening.
  breakdownType: any = 'doughnut';
  breakdownColors: string[] = [ '#B2D235', '#0095DA', '#007236', '#8CCEEE', '#555555' ] ;
  breakdownPercentages: number[] = [ 0, 0, 0, 0, 0 ] ;
  studentsGetting60Plus: number = 0 ;
  breakdownData: ChartConfiguration<'doughnut'>['data'] = {
    labels: [ '80+ minutes', '60-79 minutes', '40-59 minutes', '20-39 minutes', '0-19 minutes' ],
    datasets: [
      {
        data: [],
        backgroundColor: this.breakdownColors,
        borderWidth: 0,
      }
    ]
  } ;
  breakdownOpts: ChartConfiguration<'doughnut'>['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '56%',
    font: {
      family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
    },
    layout: {
      padding: 28,
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        align: 'end',
        anchor: 'end',
        clamp: true,
        color: '#555555',
        display: (context) => Number(context.dataset.data[context.dataIndex]) > 0,
        formatter: (value, context) => {
          let values = context.dataset.data as number[] ;
          let total = values.reduce((sum, count) => sum + Number(count), 0) ;
          return (total === 0) ? '' : `${Math.round((Number(value) / total) * 100)}%` ;
        },
        font: {
          family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
          size: 11,
        },
        offset: 4,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${this.breakdownPercentages[context.dataIndex]}%`,
        },
      },
    },
  } ;
  magnifyingGlassIcon = faMagnifyingGlass;
  enrollmentIcon = faPeopleGroup;

  tourSteps: IntroStep[] = [];

  @ViewChildren(BaseChartDirective) charts!: QueryList<BaseChartDirective> ;
  @ViewChild('usageTrendChart') trendChartCanvas!: ElementRef<HTMLCanvasElement> ;
  @ViewChild('usageTrendImg') trendChartImg!: ElementRef<HTMLImageElement> ;
  @ViewChild('breakdownChart') breakdownChartCanvas!: ElementRef<HTMLCanvasElement> ;
  @ViewChild('breakdownChartImg') breakdownChartImg!: ElementRef<HTMLImageElement> ;
  @ViewChild('printContent') printContent!: ElementRef<HTMLElement> ;

  constructor(
    private route: ActivatedRoute,
    private reportsService: ReportsService,
    private printService: PrintService,
    private sessionStorageService: SessionStorageService,
  ) { }

  ngOnInit(): void {
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser);
    this.shouldShowSchools = this.currentUser ? canChangeSchool(this.currentUser) : false;
    this.tourSteps = districtUsageIntroSteps(this.shouldShowSchools);

    // Resolve data
    this.reportData = this.route.snapshot.data['resolveData'] ;

    this.rangeData = this.reportData.rangeData.schools ;
    this.dateRanges = this.reportData.ranges ;
    if (this.dateRanges.length)
    {
      this.dateRange = this.dateRanges[0] ;
    }
    else
    {
      this.dateRange = {
        startDisplay: '-',
        endDisplay: '-',
      } ;
    }
    this.areSessionsForAcademicYear = (this.dateRanges.length > 0) ;
    this.trendData.labels = this.reportData.trendLabels ;

    // Filter data
    this.districts = this.route.snapshot.data['filterData'].districts.filter((district: District) => district.name !== 'Clever District') ;
    this.schools = this.route.snapshot.data['filterData'].schools.filter((school: School) => school.name !== 'Clever School') ;
    this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;
    this.selectedDistrictId = this.reportsService.getSelectedDistrictForReports().districtID ;
    this.selectedSchoolId = this.reportsService.getSelectedSchoolForReports().schoolID ;

    // We call filter initially on Init because we might have a school previously selected from other reports and we might
    // have to then filter based on that. The Resolver ensures that our district data is correct (it uses the selected district to load)
    this.filterSchoolTeacher({ district: this.reportsService.getSelectedDistrictForReports(), school: this.reportsService.getSelectedSchoolForReports() }) ;
  }

  toggleTeacherTableSwitch() {
    this.teacherTableSwitch = (this.teacherTableSwitch === '+') ? '-' : '+' ;
    this.showTeacherTable = !this.showTeacherTable ;
  }

  toggleSchoolTableSwitch() {
    this.schoolTableSwitch = (this.schoolTableSwitch === '+') ? '-' : '+' ;
    this.showSchoolTable = !this.showSchoolTable ;
  }

  sortSchoolTable(sortOpts: any) {
    this.schoolAggregateData.set(
      (sortOpts.sortReverse)
        ? _.sortBy(this.schoolAggregateData, this.reportsService.sortSchoolFunction, sortOpts).reverse()
        : _.sortBy(this.schoolAggregateData, this.reportsService.sortSchoolFunction, sortOpts)
    );
  }

  sortTeacherTable(sortOpts: any) {
    this.teacherAggregateData.set(
      (sortOpts.sortReverse)
        ? _.sortBy(this.teacherAggregateData, this.reportsService.sortTeacherFunction, sortOpts).reverse()
        : _.sortBy(this.teacherAggregateData, this.reportsService.sortTeacherFunction, sortOpts)
    );
  }

  filterSchoolTeacher(filterOpts: any) {
    if (filterOpts.district && filterOpts.district.districtID !== this.selectedDistrictId)
    {
      // District was changed, so we need to load new report data
      this.selectedDistrictId = filterOpts.district.districtID ;
      this.selectedSchoolId = 0 ;
      this.reportsService.getDistrictUsageData().subscribe({
        next: (reportData: any) => {
          this.reportData = reportData ;
          this.rangeData = reportData.rangeData.schools ;
          this.dateRanges = reportData.ranges ;
          if (this.dateRanges.length)
          {
            this.dateRange = this.dateRanges[0] ;
          }
          else
          {
            this.dateRange = {
              startDisplay: '-',
              endDisplay: '-',
            } ;
          }

          // Now update our data on the report
          this.setOverallUsageData(this.reportData, this.selectedSchoolId) ;
          this.setRangeData(this.rangeData) ;
        }
      }) ;
    }
    else if (filterOpts.school)
    {
      // School updated
      this.selectedSchoolId = filterOpts.school.schoolID ;
      let filteredData: any = this.reportsService.filterDistrictReportData(this.rangeData, this.selectedSchoolId) ;

      this.setOverallUsageData(this.reportData, this.selectedSchoolId) ;
      this.setRangeData(filteredData) ;
    }
  }

  loadRangeData() {
    // Clear any previous data load error, if exists
    this.loadError = '' ;

    // Now get our new range data
    this.reportsService.getDistrictUsageRangeData(this.selectedDistrictId, this.dateRange.start, this.dateRange.end).subscribe({
      next: (rangeData: any) => {
        this.rangeData = rangeData.schools ;

        let filteredData = this.reportsService.filterDistrictReportData(this.rangeData, this.selectedSchoolId) ;
        this.setRangeData(filteredData) ;
      },
      error: (err: any) => {
        this.loadError = 'There was an error while trying to load Usage data for the selected date range. ' ;
        this.loadError += 'Please try again, if the problem continues please contact us for help.'

        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'district-usage',
            districtId: this.selectedDistrictId,
            rangeStart: this.dateRange.start,
            rangeEnd: this.dateRange.end,
          }
        }) ;
      }
    }) ;
  }

  setOverallUsageData(data: any, schoolId: number): void {
    let started = 0 ;
    let enrolled = 0 ;
    let schools: any = {} ;

    if (schoolId === 0)
    {
      schools = data.schools ;
    }
    else
    {
      schools[schoolId] = data.schools[schoolId] ;
    }

    Object.values(schools).forEach((school: any) => {
      if (school)
      {
        started += school.started ;
        enrolled += school.enrolled ;
      }
    }) ;

    // Update our labels for the new school with their sessions
    let trendLabels: any[] = [] ;
    data.weeklyUsage.forEach((val: any, idx: number) => {
      trendLabels.push(idx + 1) ;
    }) ;
    this.trendData.labels = trendLabels ;

    let startedPercent = (enrolled === 0) ? 0 : Math.round((started / enrolled) * 100) ;
    this.startedPercent = (startedPercent === 0 && enrolled > 0) ? '<1' : `${startedPercent}` ;
    this.enrolled = enrolled ;
    this.trendData.datasets[0].data = Array(data.weeklyUsage.length).fill(80) ;
    this.trendData.datasets[1].data = Array(data.weeklyUsage.length).fill(60) ;

    // For the usage trend, we need to handle the option that the selected school had no data, in that case make it 0's
    if (schoolId === 0)
    {
      this.trendData.datasets[2].data = data.weeklyUsage ;
    }
    else if (data.schools[schoolId])
    {
      this.trendData.datasets[2].data = data.schools[schoolId].weeklyUsage ;
    }
    else
    {
      this.trendData.datasets[2].data = Array(data.weeklyUsage.length).fill(0) ;
    }

    if (this.charts)
    {
      this.charts.first.update() ;
    }
  }

  setRangeData(data: any): void {
    // Set our school and teacher aggregate data
    let totalUsage = 0 ;
    let totalStudents = 0 ;
    let totalStudentsWithLogin = 0 ;
    let totalStudentsWithMultiLogin = 0 ;
    let schoolData: any[] = [] ;
    let teacherData: any[] = [] ;
    let breakdownData = [ 0, 0, 0, 0, 0 ] ;
    Object.values(data).forEach((school: any) => {
      let schoolStudents = 0 ;
      let schoolStudentsMeetingMin = 0 ;
      let schoolStudentsMeetingUnits = 0;

      Object.values(school.teachers).forEach((teacher: any) => {
        schoolStudents += teacher.totalStudents ;
        schoolStudentsMeetingMin += teacher.totalStudentsMeetingMin ;
        schoolStudentsMeetingUnits += teacher.totalStudentsMeetingUnit;

        // Determine the icon to use for our percentage for each teacher
        let teacherMeetingMinutesPercent = (teacher.totalStudents === 0) ? 0 : Math.round((teacher.totalStudentsMeetingMin / teacher.totalStudents) * 100) ;
        let teacherMeetingMinutesPercentDisplay = (teacherMeetingMinutesPercent === 0 && teacher.totalStudentsMeetingMin > 0) ? '<1' : teacherMeetingMinutesPercent ;
        let teacherMeetingMinutesIcon = '' ;
        if (teacherMeetingMinutesPercent <= 60)
        {
          teacherMeetingMinutesIcon = '<img src="/assets/images/reportIcons/iconLegendRed.svg" class="icon" />' ;
        }
        else if (teacherMeetingMinutesPercent <= 75)
        {
          teacherMeetingMinutesIcon = '<img src="/assets/images/reportIcons/iconLegendYellow.svg" class="icon" />' ;
        }
        else
        {
          teacherMeetingMinutesIcon = '<img src="/assets/images/reportIcons/iconLegendGreen.svg" class="icon" />' ;
        }

        let teacherMeetingUnitsPercent = (teacher.totalStudents === 0) ? 0 : Math.round((teacher.totalStudentsMeetingUnit / teacher.totalStudents) * 100) ;
        let teacherMeetingUnitsPercentDisplay = (teacherMeetingUnitsPercent === 0 && teacher.totalStudentsMeetingUnit > 0) ? '<1' : teacherMeetingUnitsPercent ;
        let teacherMeetingUnitsIcon = '' ;
        if (teacherMeetingUnitsPercent <= 60)
        {
          teacherMeetingUnitsIcon = '<img src="/assets/images/reportIcons/iconLegendRed.svg" class="icon" />' ;
        }
        else if (teacherMeetingUnitsPercent <= 75)
        {
          teacherMeetingUnitsIcon = '<img src="/assets/images/reportIcons/iconLegendYellow.svg" class="icon" />' ;
        }
        else
        {
          teacherMeetingUnitsIcon = '<img src="/assets/images/reportIcons/iconLegendGreen.svg" class="icon" />' ;
        }

        teacherData.push({
          data: [
            teacher.firstName,
            teacher.lastName,
            teacher.grades,
            school.name,
            teacher.numberOfLogins,
            teacherMeetingMinutesPercent,
            teacherMeetingUnitsPercent,
          ],
          display: [
            teacher.firstName,
            teacher.lastName,
            teacher.grades.join(', '),
            school.name,
            teacher.numberOfLogins,
            `<div class="icon-score">${teacherMeetingMinutesIcon} <div class="score">${teacherMeetingMinutesPercentDisplay}%</div></div>`,
            `<div class="icon-score">${teacherMeetingUnitsIcon} <div class="score">${teacherMeetingUnitsPercentDisplay}%</div></div>`,
          ],
        }) ;

        // We need to go through all students and bin their usage
        Object.values(teacher.students).forEach((student: any) => {
          // If we are displaying breakdown data for the 'All Ranges' category, then we average a students usage by number of weeks
          let studentUsage = student.usage ;

          if (studentUsage < 20) breakdownData[0]++ ;
          else if (studentUsage < 40) breakdownData[1]++ ;
          else if (studentUsage < 60) breakdownData[2]++ ;
          else if (studentUsage < 80) breakdownData[3]++ ;
          else if (studentUsage >= 80) breakdownData[4]++ ;

          // Increment aggregate statistics
          totalStudents++ ;
          totalUsage += studentUsage ;
          if (student.sessionCount > 0) totalStudentsWithLogin++ ;
          if (student.sessionCount > 1) totalStudentsWithMultiLogin++ ;
        }) ;
      }) ;

      // Determine the icon to use for our percentage for each school
      let schoolMeetingMinutesPercent = (schoolStudents === 0) ? 0 : Math.round((schoolStudentsMeetingMin / schoolStudents) * 100) ;
      let schoolMeetingMinutesPercentDisplay = (schoolMeetingMinutesPercent === 0 && schoolStudentsMeetingMin > 0) ? '<1' : schoolMeetingMinutesPercent ;
      let schoolMeetingMinutesIcon = '' ;
      if (schoolMeetingMinutesPercent < 60)
      {
        schoolMeetingMinutesIcon = '<img src="/assets/images/reportIcons/iconLegendRed.svg" class="icon" />' ;
      }
      else if (schoolMeetingMinutesPercent < 75)
      {
        schoolMeetingMinutesIcon = '<img src="/assets/images/reportIcons/iconLegendYellow.svg" class="icon" />' ;
      }
      else
      {
        schoolMeetingMinutesIcon = '<img src="/assets/images/reportIcons/iconLegendGreen.svg" class="icon" />' ;
      }

      let schoolMeetingUnitsPercent = (schoolStudents === 0) ? 0 : Math.round((schoolStudentsMeetingUnits / schoolStudents) * 100) ;
      let schoolMeetingUnitsPercentDisplay = (schoolMeetingUnitsPercent === 0 && schoolStudentsMeetingUnits > 0) ? '<1' : schoolMeetingUnitsPercent ;
      let schoolMeetingUnitsIcon = '' ;
      if (schoolMeetingUnitsPercent < 60)
      {
        schoolMeetingUnitsIcon = '<img src="/assets/images/reportIcons/iconLegendRed.svg" class="icon" />' ;
      }
      else if (schoolMeetingUnitsPercent < 75)
      {
        schoolMeetingUnitsIcon = '<img src="/assets/images/reportIcons/iconLegendYellow.svg" class="icon" />' ;
      }
      else
      {
        schoolMeetingUnitsIcon = '<img src="/assets/images/reportIcons/iconLegendGreen.svg" class="icon" />' ;
      }

      schoolData.push({
        data: [
          school.name,
          Object.keys(school.teachers).length,
          Object.values(school.teachers).reduce((prev: any, cur: any) => { return prev += Object.keys(cur.students).length }, 0),
          schoolMeetingMinutesPercent,
          schoolMeetingUnitsPercent,
        ],
        display: [
          school.name,
          Object.keys(school.teachers).length,
          Object.values(school.teachers).reduce((prev: any, cur: any) => { return prev += Object.keys(cur.students).length }, 0),
          `<div class="icon-score">${schoolMeetingMinutesIcon} <div class="score">${schoolMeetingMinutesPercentDisplay}%</div></div>`,
          `<div class="icon-score">${schoolMeetingUnitsIcon} <div class="score">${schoolMeetingUnitsPercentDisplay}%</div></div>`,
        ],
      }) ;
    }) ;

    this.schoolAggregateData.set(schoolData);
    this.teacherAggregateData.set(teacherData);
    // Display the donut and its key from highest to lowest usage, matching the district dashboard.
    let orderedBreakdownData = [ breakdownData[4], breakdownData[3], breakdownData[2], breakdownData[1], breakdownData[0] ] ;
    this.breakdownPercentages = orderedBreakdownData.map(
      (count) => (totalStudents === 0) ? 0 : Math.round((count / totalStudents) * 100)
    ) ;
    this.studentsGetting60Plus = this.breakdownPercentages[0] + this.breakdownPercentages[1] ;
    this.breakdownData.datasets[0].data = orderedBreakdownData ;
    this.avgWeeklyUsage = (this.enrolled === 0) ? 0 : Math.round(totalUsage / this.enrolled) ;
    if (this.charts) this.charts.last.update() ;

    let loginPercent = (this.enrolled === 0) ? 0 : Math.round((totalStudentsWithLogin / this.enrolled) * 100) ;
    this.loginPercentDisplay = (loginPercent === 0 && totalStudentsWithLogin > 0) ? '<1' : `${loginPercent}` ;

    let multiLoginPercent = (this.enrolled === 0) ? 0 : Math.round((totalStudentsWithMultiLogin / this.enrolled) * 100) ;
    this.multiLoginPercentDisplay = (multiLoginPercent === 0 && totalStudentsWithMultiLogin > 0) ? '<1' : `${multiLoginPercent}` ;
  }

  printReport() {
    // Convert charts to images before printing
    let trendCanvas = this.trendChartCanvas.nativeElement;
    let trendImg = this.trendChartImg.nativeElement;
    let breakdownCanvas = this.breakdownChartCanvas.nativeElement;
    let breakdownImg = this.breakdownChartImg.nativeElement;
    this.reportsService.convertCanvasToImage(trendCanvas, trendImg);
    this.reportsService.convertCanvasToImage(breakdownCanvas, breakdownImg);

    let school = this.reportsService.getSelectedSchoolForReports().name;
    let teacher = this.reportsService.getSelectedTeacherForReports();
    let teacherName = `${teacher.firstName} ${teacher.lastName}`;
    this.printService.openPrintWindow(this.printContent.nativeElement, school, teacherName);
  }
}
